import { useEffect } from "react";
import { Circle, MapContainer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";

import { AttributionBadge, BaseTiles } from "@/features/map/shared";
import { MARKER_ICONS, MOSCOW } from "@/features/map/mapUtils";
import type { PlayRequest } from "./api";

import "leaflet/dist/leaflet.css";

const BRAND = "#FF5A1F";

/**
 * Static mini-map on the «Ищем» card: the picked spots and, around each,
 * the radius in which the matcher currently accepts *other players' spots*.
 * It widens while the search waits, so the circles grow on the next poll.
 */
export function SearchRadiusMap({ request }: { request: PlayRequest }) {
  const meters = request.radius_km * 1000;
  return (
    <div>
      <div
        style={{
          position: "relative",
          height: 200,
          borderRadius: "var(--r-md)",
          overflow: "hidden",
          isolation: "isolate",
          marginTop: 12,
        }}
      >
        <MapContainer
          center={MOSCOW}
          zoom={11}
          // Fractional zoom lets the circles fill the frame instead of
          // snapping a whole level out.
          zoomSnap={0.25}
          zoomControl={false}
          attributionControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          style={{ height: "100%", width: "100%" }}
        >
          <BaseTiles />
          {request.spots.map((s) => (
            <Circle
              key={`c${s.id}`}
              center={[s.latitude, s.longitude]}
              radius={meters}
              pathOptions={{ color: BRAND, weight: 1.5, fillColor: BRAND, fillOpacity: 0.12 }}
            />
          ))}
          {request.spots.map((s) => (
            <Marker
              key={`m${s.id}`}
              position={[s.latitude, s.longitude]}
              icon={MARKER_ICONS.outdoor_spot}
              interactive={false}
            />
          ))}
          <FitCircles request={request} />
        </MapContainer>
        <AttributionBadge />
      </div>
      <div className="caption" style={{ color: "var(--fg2)", marginTop: 8 }}>
        {radiusText(request)}
      </div>
      <div className="caption" style={{ color: "var(--fg3)", marginTop: 4 }}>
        Круг — это не расстояние до игроков: в нём мы ищем споты, на которых
        другие хотят играть. Если чей-то выбранный спот попадёт в круг — сведём вас.
      </div>
    </div>
  );
}

function radiusText(r: PlayRequest): string {
  const now = r.radius_km < 1
    ? "Сейчас ищем только на выбранных спотах"
    : `Сейчас ищем споты в ${formatKm(r.radius_km)} от выбранных`;
  if (r.radius_next_km == null || r.radius_next_at == null) {
    return `${now} — это максимум.`;
  }
  const at = dayjs(r.radius_next_at);
  const mins = Math.max(1, Math.ceil(at.diff(dayjs(), "minute", true)));
  return `${now}. Через ${mins} мин расширим до ${formatKm(r.radius_next_km)}.`;
}

function formatKm(km: number): string {
  return `${Number.isInteger(km) ? km : km.toFixed(1)} км`;
}

/** Zoom to show every circle whole; re-runs when the radius grows. */
function FitCircles({ request }: { request: PlayRequest }) {
  const map = useMap();
  const key = `${request.radius_km}:${request.spots.map((s) => s.id).join(",")}`;
  useEffect(() => {
    let bounds: L.LatLngBounds | null = null;
    for (const s of request.spots) {
      // toBounds(size) is a square with side `size` metres → the diameter.
      const b = L.latLng(s.latitude, s.longitude).toBounds(request.radius_km * 2000);
      bounds = bounds ? bounds.extend(b) : b;
    }
    if (!bounds) return;
    const fit = () => {
      // The card lays out after Leaflet measured the container; re-measure
      // first or the fit uses a stale (taller) size and clips the circles.
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [12, 12], animate: false });
    };
    fit();
    const t = window.setTimeout(fit, 150);
    return () => window.clearTimeout(t);
    // `key` captures everything that changes the circles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}
